/**
 * Quantities of record (ADR-0003).
 *
 * Milk and other mass-measured material: integer grams.
 * Counted goods (bottles, crates, finished units): integer units.
 * Money: integer paise (INR minor units).
 *
 * Every conversion is explicit and integer-safe. Floating point never touches a quantity of record,
 * because 0.1 * 1000 = 100.00000000000001 (the field team's documented reason, SX-B3 F-24).
 */

declare const gramsBrand: unique symbol;
declare const unitsBrand: unique symbol;
declare const paiseBrand: unique symbol;

export type Grams = number & { readonly [gramsBrand]: 'Grams' };
export type Units = number & { readonly [unitsBrand]: 'Units' };
export type Paise = number & { readonly [paiseBrand]: 'Paise' };

export class QuantityError extends Error {
  constructor(
    readonly code: 'NOT_INTEGER' | 'NEGATIVE' | 'UNSAFE' | 'PARSE' | 'COMMA' | 'PRECISION',
    message: string,
  ) {
    super(message);
    this.name = 'QuantityError';
  }
}

function assertSafeInteger(value: number, what: string): void {
  if (!Number.isFinite(value) || !Number.isInteger(value)) {
    throw new QuantityError('NOT_INTEGER', `${what} must be an integer, got ${value}`);
  }
  if (!Number.isSafeInteger(value)) {
    throw new QuantityError('UNSAFE', `${what} exceeds the safe integer range`);
  }
}

export function grams(value: number): Grams {
  assertSafeInteger(value, 'Grams');
  return value as Grams;
}

export function units(value: number): Units {
  assertSafeInteger(value, 'Units');
  return value as Units;
}

export function paise(value: number): Paise {
  assertSafeInteger(value, 'Paise');
  return value as Paise;
}

/**
 * Parse a kilogram string exactly into grams, without floating point.
 * "12.5" → 12500, "0.05" → 50, "7" → 7000. A comma is refused (ambiguous: "1,5").
 * More than three decimals is refused rather than rounded (a gram is the unit of record).
 */
export function parseKgToGrams(input: string): Grams {
  const text = input.trim();
  if (text.includes(',')) {
    throw new QuantityError('COMMA', 'Use a decimal point, not a comma, for kilograms');
  }
  const match = /^(\d+)(?:\.(\d{1,3}))?$/.exec(text);
  if (!match) {
    if (/^\d+\.\d{4,}$/.test(text)) {
      throw new QuantityError('PRECISION', 'Kilograms carry at most three decimals (one gram)');
    }
    throw new QuantityError('PARSE', `Not a kilogram value: "${input}"`);
  }
  const whole = match[1] ?? '0';
  const fraction = (match[2] ?? '').padEnd(3, '0');
  const value = Number(whole) * 1000 + Number(fraction);
  return grams(value);
}

/** Integer kilograms and remainder grams, for display without floating point drift. */
export function splitKg(g: Grams): { kg: number; g: number; negative: boolean } {
  const negative = g < 0;
  const abs = Math.abs(g);
  return { kg: Math.trunc(abs / 1000), g: abs % 1000, negative };
}

/** "12.5 kg" style text with up to `decimals` fractional digits, trailing zeros trimmed. */
export function formatKg(g: Grams | null | undefined, decimals: 0 | 1 | 2 | 3 = 1): string {
  if (g === null || g === undefined) return '—';
  const { kg, g: rem, negative } = splitKg(g);
  let fractionText = '';
  if (decimals > 0) {
    const scaled = Math.round(rem / 10 ** (3 - decimals));
    let whole = kg;
    let frac = scaled;
    if (frac >= 10 ** decimals) {
      whole += 1;
      frac = 0;
    }
    fractionText = frac === 0 ? '' : `.${String(frac).padStart(decimals, '0').replace(/0+$/, '')}`;
    return `${negative ? '−' : ''}${whole.toLocaleString('en-IN')}${fractionText} kg`;
  }
  const rounded = kg + (rem >= 500 ? 1 : 0);
  return `${negative ? '−' : ''}${rounded.toLocaleString('en-IN')} kg`;
}

/**
 * Litres are a DERIVED display value only (ADR-0003). The factor is versioned configuration
 * (e.g. L_PER_KG = 0.971, PENDING CLIENT). The result is always labelled as derived.
 */
export function deriveLitres(
  g: Grams,
  factor: { litresPerKg: number; version: string },
): { litres: number; basis: 'derived'; factorVersion: string } {
  const litres = Math.round((g / 1000) * factor.litresPerKg * 1000) / 1000;
  return { litres, basis: 'derived', factorVersion: factor.version };
}

/** ₹1,23,456.78 in Indian digit grouping. */
export function formatInr(p: Paise | null | undefined, options: { showPaise?: boolean } = {}): string {
  if (p === null || p === undefined) return '—';
  const negative = p < 0;
  const abs = Math.abs(p);
  const rupees = Math.trunc(abs / 100);
  const rem = abs % 100;
  const showPaise = options.showPaise ?? rem !== 0;
  const text = rupees.toLocaleString('en-IN') + (showPaise ? `.${String(rem).padStart(2, '0')}` : '');
  return `${negative ? '−' : ''}₹${text}`;
}

/** Parse "1,234.50" / "1234.5" rupees into paise exactly. */
export function parseRupeesToPaise(input: string): Paise {
  const text = input.trim().replace(/,/g, '').replace(/^₹/, '');
  const match = /^(-)?(\d+)(?:\.(\d{1,2}))?$/.exec(text);
  if (!match) throw new QuantityError('PARSE', `Not a rupee amount: "${input}"`);
  const sign = match[1] ? -1 : 1;
  const value = Number(match[2]) * 100 + Number((match[3] ?? '').padEnd(2, '0'));
  return paise(sign * value);
}

/** Rounding policies for converting a mass into billable units. UNDECIDED refuses (RTC_UNDECIDED_POLICY). */
export type RoundingPolicy = 'UNDECIDED' | 'FLOOR' | 'NEAREST_HALF_UP' | 'CEIL';

export class UndecidedPolicyError extends Error {
  readonly code = 'RTC_UNDECIDED_POLICY';
  constructor(policy: string) {
    super(`Policy ${policy} is still pending a client decision; the operation is refused rather than guessed.`);
    this.name = 'UndecidedPolicyError';
  }
}

/**
 * Whole billable units in a mass, given a unit's defined mass (e.g. 900 g large bottle, 1030 g farm unit).
 * Returns the remainder so nothing is silently given away (SX-B2 BR-9: 50,000 g ÷ 900 g = 55 units + 500 g).
 */
export function billableUnits(
  massG: Grams,
  unitMassG: Grams,
  policy: RoundingPolicy,
): { units: Units; remainderG: Grams } {
  if (policy === 'UNDECIDED') throw new UndecidedPolicyError('billing rounding (Q-10)');
  if (unitMassG <= 0) throw new QuantityError('NEGATIVE', 'Unit mass must be positive');
  if (massG < 0) throw new QuantityError('NEGATIVE', 'Mass must not be negative');
  const whole = Math.trunc(massG / unitMassG);
  const remainder = massG - whole * unitMassG;
  let count = whole;
  if (policy === 'CEIL' && remainder > 0) count += 1;
  if (policy === 'NEAREST_HALF_UP' && remainder * 2 >= unitMassG) count += 1;
  const remainderG = massG - count * unitMassG;
  return { units: units(count), remainderG: grams(remainderG) };
}

/** Temperatures: integer tenths of °C (null = not read; never 0). */
export function formatTempC10(c10: number | null | undefined): string {
  if (c10 === null || c10 === undefined) return '—';
  const negative = c10 < 0;
  const abs = Math.abs(c10);
  return `${negative ? '−' : ''}${Math.trunc(abs / 10)}.${abs % 10} °C`;
}
